import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Search, CornerDownLeft, History } from 'lucide-react';
import { ALL_TOOLS, TOOL_GROUPS, type ToolDef } from '../../tools';

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (id: string) => void;
  recentIds: string[];
}

interface Section {
  id: string;
  label: string;
  tools: ToolDef[];
}

export default function CommandPalette({
  open,
  onClose,
  onSelect,
  recentIds,
}: Props) {
  const [query, setQuery] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlighted(0);
      // Focus after the overlay renders
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const sections = useMemo<Section[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const recents = recentIds
        .map((id) => ALL_TOOLS.find((t) => t.id === id))
        .filter((t): t is ToolDef => Boolean(t));
      const base: Section[] = TOOL_GROUPS.map((g) => ({
        id: g.id,
        label: g.label,
        tools: g.tools,
      }));
      return recents.length > 0
        ? [{ id: 'recent', label: 'Recent', tools: recents }, ...base]
        : base;
    }
    return TOOL_GROUPS.map((g) => ({
      id: g.id,
      label: g.label,
      tools: g.tools.filter((t) =>
        (t.name + ' ' + t.shortLabel + ' ' + t.description)
          .toLowerCase()
          .includes(q)
      ),
    })).filter((s) => s.tools.length > 0);
  }, [query, recentIds]);

  const flatItems = useMemo(
    () =>
      sections.flatMap((s) =>
        s.tools.map((t) => ({ sectionId: s.id, tool: t }))
      ),
    [sections]
  );

  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  useEffect(() => {
    const el = listRef.current?.querySelector('[data-highlighted="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, sections]);

  if (!open) return null;

  const pick = (id: string) => {
    onSelect(id);
    onClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = flatItems[highlighted];
      if (item) pick(item.tool.id);
    }
  };

  let runningIndex = -1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/50 p-4 pt-[12vh] backdrop-blur-sm"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label="Tool search"
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white/95 shadow-2xl backdrop-blur-xl animate-scale-in dark:border-white/10 dark:bg-navy-900/95 dark:shadow-black/60"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-slate-200/90 px-4 py-3 dark:border-white/[0.07]">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a tool..."
            className="flex-1 bg-transparent text-sm text-slate-900 placeholder-slate-400 focus:outline-none dark:text-slate-100 dark:placeholder-slate-500"
            spellCheck={false}
          />
          <span className="kbd">Esc</span>
        </div>
        <div ref={listRef} className="max-h-[46vh] overflow-y-auto p-2">
          {flatItems.length === 0 && (
            <div className="px-3 py-10 text-center text-sm text-slate-500 dark:text-slate-400">
              No tools match "{query}".
            </div>
          )}
          {sections.map((s) => (
            <div key={s.id} className="mb-1">
              <div className="flex items-center gap-1.5 px-2.5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400 dark:text-slate-500">
                {s.id === 'recent' && <History className="h-3 w-3" />}
                {s.label}
              </div>
              {s.tools.map((t) => {
                runningIndex += 1;
                const index = runningIndex;
                const Icon = t.icon;
                const isHighlighted = index === highlighted;
                return (
                  <button
                    key={`${s.id}-${t.id}`}
                    data-highlighted={isHighlighted}
                    onClick={() => pick(t.id)}
                    onMouseEnter={() => setHighlighted(index)}
                    className={`flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors duration-100 ${
                      isHighlighted
                        ? 'bg-gradient-to-r from-sky-500/15 to-indigo-500/10 text-slate-900 dark:text-white'
                        : 'text-slate-600 dark:text-slate-300'
                    }`}
                  >
                    <span
                      className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg border transition-colors ${
                        isHighlighted
                          ? 'border-sky-500/30 bg-sky-500/10 text-sky-500 dark:text-sky-300'
                          : 'border-slate-200 bg-slate-50 text-slate-400 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-slate-400'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {t.name}
                      </span>
                      <span className="block truncate text-xs text-slate-400 dark:text-slate-500">
                        {t.description}
                      </span>
                    </span>
                    {isHighlighted && (
                      <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 border-t border-slate-200/90 px-4 py-2.5 text-[11px] text-slate-400 dark:border-white/[0.07] dark:text-slate-500">
          <span className="flex items-center gap-1">
            <span className="kbd">↑</span>
            <span className="kbd">↓</span> navigate
          </span>
          <span className="flex items-center gap-1">
            <span className="kbd">↵</span> open
          </span>
          <span className="ml-auto">
            {ALL_TOOLS.length} tools · 100% offline
          </span>
        </div>
      </div>
    </div>
  );
}
